import React from 'react';
import { Message } from '@/store/chatStore';
import { formatTime } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const { content, isMe, timestamp, type, flagged } = message;

  // System messages — editorial horizontal-rule divider
  if (type === 'SYSTEM') {
    return (
      <div className="bc-sys-line">
        <span className="bc-sys-rule" />
        <span>{content}</span>
        <span className="bc-sys-rule" />
      </div>
    );
  }

  // Attachment messages
  if (type === 'ATTACHMENT' && message.attachment) {
    const { dataUrl, mimeType, filename } = message.attachment;
    const isVideo = mimeType.startsWith('video/');
    return (
      <div className={cn('bc-bubble-row', isMe ? 'mine' : 'theirs')}>
        {!isMe && <div className="bc-bubble-tag">{formatTime(timestamp)}</div>}
        <div className={cn('bc-bubble', isMe ? 'mine' : 'theirs')} style={{ padding: 4 }}>
          <div className="overflow-hidden rounded-[14px]">
            {isVideo ? (
              <video controls className="max-w-[240px] max-h-[200px] rounded-[14px] block" src={dataUrl} />
            ) : (
              <img
                alt={filename}
                className="max-w-[240px] max-h-[200px] rounded-[14px] object-cover cursor-pointer hover:opacity-90 block"
                style={{ transition: 'opacity 180ms cubic-bezier(0.23, 1, 0.32, 1)' }}
                src={dataUrl}
                onClick={() => window.open(dataUrl, '_blank')}
              />
            )}
          </div>
          <p className="text-[11px] mt-1 px-1 opacity-70">{filename}</p>
        </div>
        {isMe && <div className="bc-bubble-tag mine">{formatTime(timestamp)}</div>}
      </div>
    );
  }

  return (
    <div className={cn('bc-bubble-row', isMe ? 'mine' : 'theirs')}>
      {!isMe && <div className="bc-bubble-tag">{formatTime(timestamp)}</div>}
      <div className={cn('bc-bubble', isMe ? 'mine' : 'theirs')}>
        {flagged && (
          <div className="flex items-center gap-1 mb-1 text-yellow-400">
            <AlertTriangle size={10} />
            <span className="text-[10px] uppercase tracking-wider">Flagged</span>
          </div>
        )}
        {content}
      </div>
      {isMe && <div className="bc-bubble-tag mine">{formatTime(timestamp)}</div>}
    </div>
  );
}
